import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useAddresses } from '@/composables/useAddresses'
import { ORDER_TYPE, type OrderType, useDelivery } from '@/composables/useDelivery'
import { type LngLat, useLocation } from '@/composables/useLocation'
import { useRegions } from '@/composables/useRegions'
import { useRestaurants } from '@/composables/useRestaurants'
import { useUserStore } from '@/stores/user'

/** Снимок состояния локации — чтобы откатиться, если адрес вне зоны доставки. */
export interface LocationSnapshot {
  delivery: OrderType
  coords: LngLat
  restaurantId: string
  /** Регион тоже откатываем: адрес из другого города меняет и его */
  region: number
  addressName: string
  /** Выбранная запись справочника — без неё откат терял привязку к адресу */
  addressId: number
}

export { ORDER_TYPE }

/**
 * Локация: тип получения → регион → ресторан → координаты (§4 LOGIC.md).
 * Собирает пять композаблов, состояние лежит в cookie и localStorage,
 * поэтому переживает перезагрузку и совпадает с веб-версией.
 */
export const useLocationStore = defineStore('location', () => {
  const userStore = useUserStore()

  const { activeDelivery, isDelivery, deliveryOptions, setActiveDelivery } = useDelivery()

  const {
    regions,
    loading: regionsLoading,
    activeRegion,
    currentRegionId,
    regionCenter,
    setRegionId,
    setRegionCenter,
    getRegions,
  } = useRegions()

  const {
    restaurantId,
    restaurants,
    markers,
    activeRestaurant,
    loading: restaurantsLoading,
    setRestaurantId,
    getRestaurants,
  } = useRestaurants()

  const {
    longLat,
    hasCoordinates,
    addressName,
    addressId,
    setLongLat,
    setAddressName,
    setAddressId,
  } = useLocation()

  const {
    addresses,
    isSaving: isAddressSaving,
    setList: setAddresses,
    loadAddresses,
    findNearest,
    addAddress,
    updateAddress,
    removeAddress,
  } = useAddresses()

  /** Регион как строка — в таком виде его ждёт запрос меню. */
  const regionId = computed(() => String(currentRegionId.value))

  /**
   * Филиал, по которому надо подтянуть регион. Сам регион клиент нигде не
   * выбирает: в cookie он лежит с дефолтом «Ташкент», а кто готовит заказ,
   * решает бэкенд по координатам. Без этой сверки клиент из Ферганы получал
   * на самовывозе ташкентские филиалы, а заказ уходил с чужим `regionId`.
   */
  const regionSource = ref('')

  /**
   * Регион берём у филиала, который выбран для заказа: у доставки его
   * называет бэкенд вместе с персональным меню, у самовывоза — сам клиент.
   */
  function syncRegionByRestaurant(id: string): void {
    regionSource.value = id
  }

  /*
   * Ждём справочник филиалов: меню и справочники грузятся параллельно, и
   * ответ меню нередко приходит первым — тогда искать филиал ещё негде.
   */
  watch(
    [regionSource, restaurants],
    ([id, list]) => {
      if (!id) return

      const found = list.find((rest) => rest.id === id)
      if (!found) return

      if (found.regionId !== currentRegionId.value) setRegionId(found.regionId)
      regionSource.value = ''
    },
    { immediate: true },
  )

  /**
   * Запись справочника, по которой едем. Это выбор клиента, а не догадка:
   * ищем по сохранённому id. Поиск по расстоянию остался только для тех, у
   * кого id ещё не записан, — он появится, как только человек выберет адрес
   * заново. Раньше по расстоянию искали всегда, и в заказ попадала соседняя
   * запись со своим подъездом либо не попадала никакая.
   *
   * Запись удалили — остаёмся без неё: подставлять взамен соседнюю нельзя,
   * это и есть подмена адреса.
   */
  const currentAddress = computed(() => {
    if (addressId.value) return addresses.value.find((item) => item.id === addressId.value)

    return findNearest(longLat.value[0], longLat.value[1])
  })

  /**
   * Как показать текущую точку. Сохранённый адрес знает название и детали,
   * но точка может быть и не из справочника — тогда берём запомненную строку.
   */
  const currentAddressName = computed(() => {
    /* Нет координат — нет и точки: старое название показывать нельзя */
    if (!hasCoordinates.value) return ''
    return currentAddress.value?.name.trim() || addressName.value
  })

  /** Филиалы своего региона: в другом городе они клиенту не нужны. */
  const regionRestaurants = computed(() =>
    restaurants.value.filter((rest) => rest.regionId === currentRegionId.value),
  )

  const regionMarkers = computed(() =>
    markers.value.filter((marker) =>
      regionRestaurants.value.some((rest) => rest.id === marker.id),
    ),
  )

  /** Самовывоз доступен не везде — у филиала есть отдельный флаг. */
  const pickupRestaurants = computed(() =>
    regionRestaurants.value.filter((rest) => rest.allowTakeOff),
  )

  const pickupMarkers = computed(() =>
    markers.value.filter((marker) => pickupRestaurants.value.some((rest) => rest.id === marker.id)),
  )

  /** Куда смотрит карта при открытии: выбранная точка, иначе центр региона. */
  const mapCenter = computed<LngLat>(() =>
    hasCoordinates.value ? longLat.value : regionCenter.value,
  )

  /**
   * Есть ли куда везти. У доставки это точка на карте, у самовывоза — филиал:
   * координат там нет вовсе, их обнуляет подтверждение выбора.
   */
  const hasPoint = computed(() =>
    isDelivery.value ? hasCoordinates.value : Boolean(restaurantId.value),
  )

  /*
   * Выбор точки живёт в сторе, а не на главной: открыть его нужно и из меню,
   * и из избранного, и из корзины — отовсюду, где клиент берёт товар.
   */
  const isPickerOpen = ref(false)
  const pickerType = ref<OrderType>(activeDelivery.value)
  /** Начать со списка сохранённых адресов, а не с карты. */
  const isPickerSaved = ref(false)

  function openPicker(orderType: OrderType, withSaved = false): void {
    pickerType.value = orderType
    isPickerSaved.value = withSaved
    isPickerOpen.value = true
  }

  /**
   * Точка нужна раньше товара. Без неё меню показывает витрину региона, цены
   * и состав могут не совпасть с филиалом, который возьмёт заказ, — а в самом
   * заказе адреса не окажется вовсе. Поэтому вместо молчаливого добавления
   * зовём выбрать адрес: у доставки сразу списком сохранённых, если они есть.
   *
   * `true` — можно продолжать, `false` — экран выбора уже открыт.
   */
  function requirePoint(): boolean {
    if (hasPoint.value) return true

    openPicker(activeDelivery.value, true)
    return false
  }

  /** Забыть точку доставки целиком: координаты, название и ссылку на запись. */
  function clearPoint(): void {
    setLongLat([0, 0])
    setAddressName('')
    setAddressId(0)
  }

  /*
   * Точка доставки принадлежит аккаунту, а не устройству: она ссылается на
   * адрес из его справочника. Сменился клиент — забываем и точку, и филиал,
   * иначе новый хозяин телефона заказывает по адресу предыдущего, а названия
   * адреса не видит: в его справочнике такой записи нет.
   */
  watch(
    () => userStore.userId,
    (id, previous) => {
      if (!previous || id === previous) return

      clearPoint()
      setRestaurantId('')
    },
  )

  /*
   * Справочник доехал — сверяем, что выбранная запись в нём есть. Нет —
   * значит её удалили или она из чужого аккаунта: координаты без записи
   * уводят заказ не туда. Пока карточка клиента не пришла, судить не по
   * чему: пустой справочник там ещё ничего не значит.
   */
  watch([() => userStore.isProfileLoaded, addresses], ([loaded]) => {
    if (!loaded || !addressId.value) return
    if (!addresses.value.some((item) => item.id === addressId.value)) clearPoint()
  })

  /**
   * Удаление адреса из справочника. Если ехали именно по нему — снимаем
   * ссылку: подставлять взамен соседнюю запись нельзя.
   */
  async function dropAddress(id: number): Promise<boolean> {
    const ok = await removeAddress(id)
    if (ok && addressId.value === id) setAddressId(0)
    return ok
  }

  function snapshot(): LocationSnapshot {
    return {
      delivery: activeDelivery.value,
      coords: longLat.value,
      restaurantId: restaurantId.value,
      region: currentRegionId.value,
      addressName: addressName.value,
      addressId: addressId.value,
    }
  }

  function restore(value: LocationSnapshot): void {
    setActiveDelivery(value.delivery)
    setLongLat(value.coords)
    setRestaurantId(value.restaurantId)
    setRegionId(value.region)
    setAddressName(value.addressName)
    setAddressId(value.addressId)
  }

  async function loadDictionaries(): Promise<void> {
    await Promise.all([getRegions(), getRestaurants()])
  }

  return {
    activeDelivery,
    isDelivery,
    deliveryOptions,
    setActiveDelivery,

    regions,
    regionsLoading,
    activeRegion,
    currentRegionId,
    regionId,
    regionCenter,
    setRegionId,
    setRegionCenter,
    syncRegionByRestaurant,
    getRegions,

    restaurantId,
    restaurants,
    regionRestaurants,
    markers,
    regionMarkers,
    pickupRestaurants,
    pickupMarkers,
    activeRestaurant,
    restaurantsLoading,
    setRestaurantId,
    getRestaurants,

    longLat,
    hasCoordinates,
    hasPoint,
    mapCenter,
    isPickerOpen,
    pickerType,
    isPickerSaved,
    openPicker,
    requirePoint,
    addressName,
    addressId,
    currentAddressName,
    setLongLat,
    setAddressName,
    setAddressId,
    clearPoint,

    addresses,
    isAddressSaving,
    currentAddress,
    setAddresses,
    loadAddresses,
    findNearest,
    addAddress,
    updateAddress,
    removeAddress: dropAddress,

    snapshot,
    restore,
    loadDictionaries,
  }
})
